/**
 * TTS (Text-to-Speech) utilities — British English female voice with a
 * simple state machine that plays nicely with iOS Safari's audio session.
 *
 * Why this file is deliberately minimal:
 *   iOS Safari (all versions ≥15) has a well-documented freeze bug when
 *   `speechSynthesis.cancel()` is called from within a SpeechRecognition
 *   callback chain — the JS event loop can hang indefinitely, taking
 *   React re-renders and touch events down with it (that's why users
 *   reported the whole page — including Back/Next buttons — freezing
 *   after the first voice command).
 *
 * Design rules:
 *   1. NEVER call speechSynthesis.cancel() implicitly. Only `shutUp()`
 *      cancels, and only when the user explicitly asked to stop.
 *   2. Overlapping `speak()` calls queue naturally — new utterances play
 *      after the current one finishes. iOS handles this without freezing.
 *   3. `isTtsBusy()` uses ONLY our own `_ttsActive` flag (set by utterance
 *      events). We do NOT touch `speechSynthesis.speaking` because it can
 *      get stuck at `true` on iOS after audio-session churn.
 *   4. Voice-recognition callers read `isTtsBusy()` before restarting the
 *      mic; if busy they subscribe via `onTtsIdle(cb)` which fires exactly
 *      once when the current utterance ends (plus a 300ms grace so iOS
 *      can release the audio session).
 */

let _ttsActive = false;
let _ttsIdleWaiters = [];
let _fallbackTimer = null;

function _markTtsStart() {
  _ttsActive = true;
  if (_fallbackTimer) { clearTimeout(_fallbackTimer); _fallbackTimer = null; }
}
function _markTtsEnd() {
  _ttsActive = false;
  if (_fallbackTimer) { clearTimeout(_fallbackTimer); _fallbackTimer = null; }
  const waiters = _ttsIdleWaiters;
  _ttsIdleWaiters = [];
  // 300ms grace lets iOS release its audio session before the mic grabs it.
  setTimeout(() => { waiters.forEach((cb) => { try { cb(); } catch { /* ignore */ } }); }, 300);
}

export function isTtsBusy() { return _ttsActive; }

/**
 * Subscribe to be notified when TTS becomes idle. If already idle, fires
 * on the next microtask (with the 300ms audio-session grace). Callers
 * MUST unsubscribe if they no longer need the callback (returned handle).
 */
export function onTtsIdle(cb) {
  if (!_ttsActive) {
    setTimeout(cb, 50);
    return () => {};
  }
  _ttsIdleWaiters.push(cb);
  return () => {
    _ttsIdleWaiters = _ttsIdleWaiters.filter((x) => x !== cb);
  };
}

// ---------------- Voice selection (unchanged from previous fix) ----------------
const PREFERRED_BRITISH_FEMALE = [
  "Google UK English Female",
  "Microsoft Libby Online",
  "Microsoft Sonia Online",
  "Microsoft Hazel Online",
  "Microsoft Hazel",
  "Microsoft Susan",
  "Kate", "Serena", "Susan", "Sonia", "Hazel", "Amelia", "Emma", "Libby", "Stephanie",
];
const BAD_LANGS = new Set(["en-AU", "en-IE", "en-ZA", "en-IN", "en-NG"]);
const BAD_NAMES = new Set(["Karen", "Tessa", "Fiona", "Moira", "Rishi", "Veena"]);

let _cachedKey = "";
let _cachedVoice = null;

function listVoices() {
  try { return window.speechSynthesis.getVoices() || []; } catch { return []; }
}

function pickVoice() {
  const voices = listVoices();
  const key = voices.map((v) => `${v.name}|${v.lang}`).join("§");
  if (key === _cachedKey && _cachedVoice) return _cachedVoice;
  const eligible = voices.filter(
    (v) => !BAD_LANGS.has(v.lang) && !BAD_NAMES.has(v.name)
  );
  let picked = null;
  for (const name of PREFERRED_BRITISH_FEMALE) {
    picked = eligible.find((v) => v.name === name || v.name.includes(name));
    if (picked) break;
  }
  if (!picked) picked = eligible.find((v) => v.lang === "en-GB") || eligible.find((v) => v.lang?.startsWith("en-")) || null;
  _cachedKey = key;
  _cachedVoice = picked;
  return picked;
}

export function clearVoiceCache() { _cachedKey = ""; _cachedVoice = null; }

/**
 * Speak `text` with a British English female voice.
 *
 * IMPORTANT (iOS): we do NOT call speechSynthesis.cancel() here. Cancel
 * has a documented WebKit freeze bug when invoked from a recognition
 * callback chain — it can hang the entire main thread, taking React
 * updates and click handlers down with it. If you need to interrupt
 * TTS (e.g. "voice off" command), use `shutUp()` which is only called
 * from an explicit user gesture.
 */
export function speak(text) {
  try {
    if (!window.speechSynthesis || !text) return;
    const u = new SpeechSynthesisUtterance(String(text));
    u.rate = 0.92;
    u.pitch = 1.0;
    const v = pickVoice();
    if (v) { u.voice = v; u.lang = v.lang || "en-GB"; } else { u.lang = "en-GB"; }
    _markTtsStart();
    u.onend = _markTtsEnd;
    u.onerror = _markTtsEnd;
    // Safety net: some iOS builds swallow onend after audio-session churn.
    // Cap max plausible utterance at 12s so the recogniser can't be
    // blocked forever waiting on a phantom TTS session.
    _fallbackTimer = setTimeout(() => { if (_ttsActive) _markTtsEnd(); }, 12_000);
    window.speechSynthesis.speak(u);
  } catch { /* ignore */ }
}

/**
 * Hard-stop TTS. ONLY call from an explicit user gesture (mic-off button,
 * "voice off" command handler). Never call this from anywhere that runs
 * inside a SpeechRecognition callback chain — see the iOS freeze note.
 */
export function shutUp() {
  try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
  _markTtsEnd();
}

export function warmUpVoices() {
  if (!window.speechSynthesis) return;
  listVoices();
  window.speechSynthesis.addEventListener?.("voiceschanged", clearVoiceCache);
  if (typeof window !== "undefined") {
    window.__rfVoiceDebug = () => {
      const picked = pickVoice();
      const all = listVoices().map((v) => ({ name: v.name, lang: v.lang, default: v.default }));
      console.log("[RecipeFiler] picked voice:", picked ? { name: picked.name, lang: picked.lang } : null);
      console.table(all);
      return { picked, all };
    };
  }
}

export const isIOS = () => typeof navigator !== "undefined" && (/iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

// Bright rising "ping" confirming an action worked.
export function okPing() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const t = ctx.currentTime;
    [[880, 0], [1320, 0.12]].forEach(([f, o]) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.connect(g); g.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(f, t + o);
      g.gain.setValueAtTime(0, t + o);
      g.gain.linearRampToValueAtTime(0.35, t + o + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + o + 0.3);
      osc.start(t + o); osc.stop(t + o + 0.32);
    });
    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 1000);
  } catch { /* ignore */ }
}

// Low two-tone "uh-oh" so the cook knows a request wasn't actioned.
export function missBeep() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const t = ctx.currentTime;
    [[440, 0], [294, 0.18]].forEach(([f, o]) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.connect(g); g.connect(ctx.destination);
      osc.type = "triangle";
      osc.frequency.setValueAtTime(f, t + o);
      g.gain.setValueAtTime(0, t + o);
      g.gain.linearRampToValueAtTime(0.5, t + o + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + o + 0.22);
      osc.start(t + o); osc.stop(t + o + 0.25);
    });
    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 1000);
  } catch { /* ignore */ }
}

// First miss of the visit is spoken in full; every later miss is just a beep.
export function sayOnceThenBeep(msg, say = speak) {
  let told = false;
  try { told = sessionStorage.getItem("rf_voice_miss_told") === "1"; sessionStorage.setItem("rf_voice_miss_told", "1"); } catch { /* ignore */ }
  if (told) missBeep(); else say(msg);
}
